import { useRef, useState, useCallback } from 'react'

/**
 * Synchronous double-submit guard. `mutation.isPending` alone isn't enough —
 * on a slow link the click-to-state-update gap lets a user fire `mutate()`
 * twice before the button ever goes disabled. `lock()` flips a ref
 * synchronously; subsequent calls in the same tick return false and the
 * caller short-circuits. Wire `unlock` into the mutation's `onSettled`.
 *
 * Usage:
 *   const { locked, lock, unlock } = useSubmitLock()
 *   const mutation = useMutation({ mutationFn: create, onSettled: unlock })
 *   const onSubmit = (data) => { if (!lock()) return; mutation.mutate(data) }
 *   <button disabled={locked || mutation.isPending} ...>
 */
export default function useSubmitLock() {
  const lockRef = useRef(false)
  const [locked, setLocked] = useState(false)

  const lock = useCallback(() => {
    if (lockRef.current) return false
    lockRef.current = true
    setLocked(true)
    return true
  }, [])

  const unlock = useCallback(() => {
    lockRef.current = false
    setLocked(false)
  }, [])

  return { locked, lock, unlock }
}
